import { METRIC_TYPE } from '@nx/state';

import { combineOptions } from '../../plot/utils';
import type { ChartWidgetPartialProps } from '../fullstack-chart-props';
import * as tooltips from '../help-tooltips';
import {
  BigCountersConfig,
  BytesChartConfig,
  RateChartConfig,
  TimeChartConfig,
  plotOptionsCumulativeDurationsFormatter,
  plotOptionsDuplicateBytesFormatter,
} from '../helpers';

const SuccessfulQueryExecutions: ChartWidgetPartialProps = {
  title: 'Query Rate',
  subtitle: 'Successful',
  seriesName: 'Queries per minute',
  yLabel: 'Per minute',
  metricName: 'fleet_management_neo4j_db_query_execution_success_per_minute',
  metricType: METRIC_TYPE.Counter,
  chartType: 'DB',
  tooltipComponent: tooltips.QueryExecutions,
  ...RateChartConfig,
};

const FailedQueryExecutions: ChartWidgetPartialProps = {
  title: 'Query Rate',
  subtitle: 'Failed',
  seriesName: 'Queries per minute',
  yLabel: 'Per minute',
  metricName: 'fleet_management_neo4j_db_query_execution_failure_per_minute',
  metricType: METRIC_TYPE.Counter,
  chartType: 'DB',
  ...RateChartConfig,
};

const StoreSizeDatabaseChartProps: ChartWidgetPartialProps = {
  title: 'Store Size',
  subtitle: 'Allocated',
  seriesName: 'Allocated space',
  aggregations: ['MAX'],
  yLabel: 'Space',
  metricType: METRIC_TYPE.Gauge,
  metricName: 'fleet_management_neo4j_store_size_database',
  chartType: 'DB',
  ...BytesChartConfig,
  tooltipComponent: tooltips.FleetManagementStoreSizeAllocatedAndUsed,
  uplotOptions: combineOptions(BytesChartConfig.uplotOptions, plotOptionsDuplicateBytesFormatter),
};

const StoreSizeDatabaseUsedChartProps: ChartWidgetPartialProps = {
  title: 'Store Size',
  subtitle: 'Used',
  seriesName: 'Used space',
  aggregations: ['MAX'],
  yLabel: 'Space',
  metricType: METRIC_TYPE.Gauge,
  metricName: 'fleet_management_neo4j_store_size_database_used',
  chartType: 'DB',
  ...BytesChartConfig,
  tooltipComponent: tooltips.FleetManagementStoreSizeAllocatedAndUsed,
  uplotOptions: combineOptions(BytesChartConfig.uplotOptions, plotOptionsDuplicateBytesFormatter),
};

const TransactionsActiveReadChartProps: ChartWidgetPartialProps = {
  title: 'Active Transactions',
  subtitle: 'Read',
  yLabel: 'Count',
  metricName: 'fleet_management_transaction_active_read',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const TransactionsActiveWriteChartProps: ChartWidgetPartialProps = {
  title: 'Active Transactions',
  subtitle: 'Write',
  yLabel: 'Count',
  metricName: 'fleet_management_transaction_active_write',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const TransactionsCommittedChartProps: ChartWidgetPartialProps = {
  title: 'Transactions',
  subtitle: 'Committed (count)',
  yLabel: 'Count',
  metricName: 'fleet_management_transaction_committed',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const TransactionsCommittedRateChartProps: ChartWidgetPartialProps = {
  title: 'Transactions',
  subtitle: 'Committed (rate)',
  yLabel: 'Per minute',
  metricName: 'fleet_management_transaction_committed_per_minute',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...RateChartConfig,
};

const TransactionsPeakConcurrentChartProps: ChartWidgetPartialProps = {
  title: 'Transactions',
  subtitle: 'Peak Concurrent',
  yLabel: 'Count',
  metricName: 'fleet_management_transaction_peak_concurrent',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const TransactionsRollbackChartProps: ChartWidgetPartialProps = {
  title: 'Transactions',
  subtitle: 'Rollbacks (count)',
  yLabel: 'Count',
  metricName: 'fleet_management_transaction_rollbacks',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const TransactionsRollbackRateChartProps: ChartWidgetPartialProps = {
  title: 'Transactions',
  subtitle: 'Rollbacks (rate)',
  yLabel: 'Per minute',
  metricName: 'fleet_management_transaction_rollbacks_per_minute',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...RateChartConfig,
};

const TransactionLastCommittedTxIdChartProps: ChartWidgetPartialProps = {
  title: 'Transactions',
  subtitle: 'Last Committed Transaction ID',
  yLabel: 'Last ID',
  metricName: 'fleet_management_transaction_last_committed_tx_id',
  metricType: METRIC_TYPE.Counter,
  chartType: 'DB',
  ...BigCountersConfig,
};

const CypherReplanChartProps: ChartWidgetPartialProps = {
  title: 'Replan Events',
  subtitle: 'Total count',
  yLabel: 'Count',
  metricName: 'fleet_management_cypher_replan_events',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const CypherReplanRateChartProps: ChartWidgetPartialProps = {
  title: 'Replan Events',
  subtitle: 'Rate',
  yLabel: 'Per minute',
  metricName: 'fleet_management_cypher_replan_events_per_minute',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...RateChartConfig,
};

const NodeCountProps: ChartWidgetPartialProps = {
  title: 'Entities',
  subtitle: 'Nodes (count)',
  yLabel: 'Count',
  metricName: 'fleet_management_neo4j_node_count',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const NodeCountRateProps: ChartWidgetPartialProps = {
  title: 'Entities',
  subtitle: 'Nodes (rate)',
  yLabel: 'Per minute',
  metricName: 'fleet_management_neo4j_node_count_per_minute',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...RateChartConfig,
};

const RelationshipCountProps: ChartWidgetPartialProps = {
  title: 'Entities',
  subtitle: 'Relationships (count)',
  yLabel: 'Count',
  metricName: 'fleet_management_neo4j_relationship_count',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const RelationshipCountRateProps: ChartWidgetPartialProps = {
  title: 'Entities',
  subtitle: 'Relationships (rate)',
  yLabel: 'Per minute',
  metricName: 'fleet_management_neo4j_relationship_count_per_minute',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...RateChartConfig,
};

const CheckPointEventsProps: ChartWidgetPartialProps = {
  title: 'Checkpoint Events',
  subtitle: 'Total count',
  yLabel: 'Count',
  metricName: 'fleet_management_check_point_events',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...BigCountersConfig,
};

const CheckPointEventsRateProps: ChartWidgetPartialProps = {
  title: 'Checkpoint Events',
  subtitle: 'Rate',
  yLabel: 'Per minute',
  metricName: 'fleet_management_check_point_events_per_minute',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...RateChartConfig,
};

const CheckPointTotalTimeProps: ChartWidgetPartialProps = {
  title: 'Checkpoint Events',
  subtitle: 'Cumulative time',
  yLabel: 'Cumulative time',
  metricName: 'fleet_management_check_point_total_time',
  metricType: METRIC_TYPE.Counter,
  chartType: 'DB',
  ...TimeChartConfig,
  uplotOptions: combineOptions(TimeChartConfig.uplotOptions, plotOptionsCumulativeDurationsFormatter),
};

const CheckPointDurationProps: ChartWidgetPartialProps = {
  title: 'Checkpoint Events',
  subtitle: 'Duration',
  yLabel: 'Last checkpoint duration',
  metricName: 'fleet_management_check_point_duration',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...TimeChartConfig,
  uplotOptions: combineOptions(TimeChartConfig.uplotOptions, plotOptionsCumulativeDurationsFormatter),
};

const QueryLatencyQ99Props: ChartWidgetPartialProps = {
  title: 'Query Latency',
  subtitle: '99th percentile',
  yLabel: 'Duration',
  metricName: 'fleet_management_db_query_execution_latency_q99',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...TimeChartConfig,
};

const QueryLatencyQ75Props: ChartWidgetPartialProps = {
  title: 'Query Latency',
  subtitle: '75th percentile',
  yLabel: 'Duration',
  metricName: 'fleet_management_db_query_execution_latency_q75',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...TimeChartConfig,
};

const QueryLatencyQ50Props: ChartWidgetPartialProps = {
  title: 'Query Latency',
  subtitle: '50th percentile',
  yLabel: 'Duration',
  metricName: 'fleet_management_db_query_execution_latency_q50',
  metricType: METRIC_TYPE.Gauge,
  chartType: 'DB',
  ...TimeChartConfig,
};

export const DeploymentDatabaseCharts = [
  [StoreSizeDatabaseChartProps, StoreSizeDatabaseUsedChartProps],
  [SuccessfulQueryExecutions, FailedQueryExecutions],
  [QueryLatencyQ99Props, QueryLatencyQ75Props, QueryLatencyQ50Props],
  [NodeCountProps, NodeCountRateProps, RelationshipCountProps, RelationshipCountRateProps],
  [TransactionsActiveReadChartProps, TransactionsActiveWriteChartProps],
  [
    TransactionsCommittedChartProps,
    TransactionsCommittedRateChartProps,
    TransactionsPeakConcurrentChartProps,
    TransactionsRollbackChartProps,
    TransactionsRollbackRateChartProps,
    TransactionLastCommittedTxIdChartProps,
  ],
  [CypherReplanChartProps, CypherReplanRateChartProps],
  [CheckPointEventsProps, CheckPointEventsRateProps, CheckPointTotalTimeProps, CheckPointDurationProps],
];
